import { Box } from '@atlaskit/primitives';
import { EditComponentProps } from '@databases/fields/edit/types';
import { flaggingCallback, useFlaggingCallback } from '@databases/flags';
import { JiraIssueRef } from '@databases/types/confluence';
import { DatabaseFieldType } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { isNonEmptyJiraIssueValue } from '@databases/utils/database/field-types/jiraIssue';
import { generateId } from '@databases/utils/utils';
import React, { FunctionComponent, useCallback, useMemo } from 'react';

import { EditableListProps } from '../../EditableList';
import { EditableListWithPlaceholder } from '../../EditableListWithPlaceholder';
import JiraIssuePicker from '../../JiraIssuePicker';

type Value = (JiraIssueRef | object) & { _id: string };

function isNonEmptyValue(val: Value): val is JiraIssueRef & { _id: string } {
  return isNonEmptyJiraIssueValue(val);
}

const getId = (value: Value): string => value._id;

const getNewValue = (): Value => ({ _id: generateId() });

const JiraIssueValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.JIRA_ISSUE>
> = ({ field, value, onChange, onFocus, onBlur, autoFocus, isSeamless }) => {
  const resolvedValue = useMemo(
    () =>
      value.map((v) =>
        isNonEmptyJiraIssueValue(v) ? { ...v, serverId: field.settings.jiraServerId } : v
      ),
    [field.settings.jiraServerId, value]
  );
  const isMulti = field.settings.multi || resolvedValue.length > 1;

  const handleSingleChange = useFlaggingCallback(
    async (val: JiraIssueRef | undefined): Promise<void> => {
      const singleJiraIssue =
        resolvedValue.length === 0
          ? { ...(val ?? {}), _id: generateId() }
          : { ...(val ?? {}), _id: resolvedValue[0]._id };
      await onChange(val ? [singleJiraIssue] : [], {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta: val
          ? resolvedValue.length === 0
            ? {
                type: MultiValueChangeType.APPEND,
                value: singleJiraIssue,
              }
            : {
                type: MultiValueChangeType.EDIT,
                index: 0,
                value: singleJiraIssue,
              }
          : {
              type: MultiValueChangeType.REMOVE,
              index: 0,
            },
      });
    },
    [onChange, resolvedValue]
  );

  const renderField: EditableListProps<Value, null>['renderField'] = useCallback(
    ({ value, autoFocus, onChange }) => (
      <Box style={{ width: '100%' }}>
        <JiraIssuePicker
          cloudId={field.settings.cloudId}
          serverId={field.settings.jiraServerId}
          value={isNonEmptyValue(value) ? value : undefined}
          onChange={flaggingCallback((val) => {
            onChange({ ...(val ?? {}), _id: value._id }, null);
          })}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          isSeamless
          isLinkPreview
        />
      </Box>
    ),
    [field.settings.cloudId, field.settings.jiraServerId, onBlur, onFocus]
  );

  const onItemAdd = React.useCallback(
    async (val: Value, allValues: Value[]) => {
      await onChange(allValues, {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: val,
        },
      });
    },
    [onChange]
  );

  const onItemDelete = React.useCallback(
    async (val: Value, removeIndex: number, allValues: Value[]) => {
      await onChange(allValues, {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index: removeIndex,
        },
      });
    },
    [onChange]
  );

  const onItemChange = React.useCallback(
    async (val: Value, newIndex: number, delta: null, allValues: Value[]) => {
      await onChange(allValues, {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta: {
          type: MultiValueChangeType.EDIT,
          index: newIndex,
          value: val,
        },
      });
    },
    [onChange]
  );

  const onItemReorder = React.useCallback(
    async (val: Value, currentIndex: number, newIndex: number, allValues: Value[]) => {
      await onChange(allValues, {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta: {
          type: MultiValueChangeType.REORDER,
          currentIndex,
          newIndex,
        },
      });
    },
    [onChange]
  );

  if (isMulti) {
    return (
      <EditableListWithPlaceholder<Value, null>
        getId={getId}
        values={resolvedValue}
        onItemChange={onItemChange}
        onAddNewItem={onItemAdd}
        onRemoveItem={onItemDelete}
        onReorder={onItemReorder}
        getNewValue={getNewValue}
        renderField={renderField}
        autoFocus={value.length === 0 ? autoFocus : undefined}
        addLabel="Add issue"
        noAdd={!field.settings.multi}
      />
    );
  } else {
    return (
      <JiraIssuePicker
        cloudId={field.settings.cloudId}
        serverId={field.settings.jiraServerId}
        value={
          resolvedValue.length > 0
            ? isNonEmptyValue(resolvedValue[0])
              ? resolvedValue[0]
              : undefined
            : undefined
        }
        onChange={handleSingleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        isClearable
        isSeamless={isSeamless}
        isLinkPreview
      />
    );
  }
};

export default JiraIssueValueEdit;
